import { useState } from "react"
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    ClickAwayListener,
    Divider,
    Fade,
    IconButton,
    Stack,
    Typography,
} from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { skillMaps } from "routes/skill-mapper/controller/skill-maps"
import { rateCards } from "routes/rate-cards/controller/rate-cards"
import { scenarios } from "routes/scenarios/controller/scenarios"
import { navigate } from "lib/routes/navigate"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { useReferenceState } from "lib/@hooks/use-reference"
import { useCreateRateCard } from "./plugins/rate-cards/use-create-rate-card"
import { useCreateSkillMap } from "./plugins/skill-mappings/use-create-skill-map"
import { GetStartedStepRow } from "./get-started-step-row"

export function GetStartedFab({ defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen)
    const [dismissed, setDismissed] = useReferenceState("optimiser-get-started-dismissed", false)
    const skillMapResult = skillMaps.useResults({ skip: 0, take: 1 })
    const rateCardResult = rateCards.useResults({ skip: 0, take: 1 })
    const scenarioResult = scenarios.useResults({ skip: 0, take: 1 })
    const hasSkillMaps = (skillMapResult?.totalCount ?? 0) > 0
    const hasRateCards = (rateCardResult?.totalCount ?? 0) > 0
    const hasScenarios = (scenarioResult?.totalCount ?? 0) > 0
    const handleCreateRateCard = useCreateRateCard()
    const handleCreateSkillMap = useCreateSkillMap()
    const completedSteps = (hasSkillMaps ? 1 : 0) + (hasRateCards ? 1 : 0) + (hasScenarios ? 1 : 0)
    const allComplete = hasSkillMaps && hasRateCards && hasScenarios

    if (dismissed && allComplete) return null

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1200 }}>
                <Fade in={open}>
                    <Card
                        sx={{
                            position: "absolute",
                            bottom: 56,
                            right: 0,
                            width: 440,
                            borderRadius: "12px",
                            boxShadow: "0px 12px 24px -4px rgba(145,158,171,0.2)",
                        }}
                    >
                        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2.5, py: 2.5 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, fontSize: 18 }}>
                                    Get Started
                                </Typography>
                                <If value={allComplete}>
                                    <Chip
                                        size="small"
                                        color="primary"
                                        icon={<Iconify icon="eva:checkmark-circle-2-fill" width={16} />}
                                        label="Complete"
                                        sx={{ fontWeight: 600 }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={handleDismiss}
                                        data-cy="get-started-fab-dismiss"
                                        aria-label="Dismiss"
                                    >
                                        <Iconify icon="mdi:close" width={20} />
                                    </IconButton>
                                    <Else>
                                        <Typography variant="body2" color="text.secondary">
                                            {completedSteps} of 3 steps complete
                                        </Typography>
                                        <Box sx={{ position: "relative", display: "inline-flex" }}>
                                            <CircularProgress
                                                variant="determinate"
                                                value={100}
                                                size={28}
                                                thickness={4}
                                                sx={{ color: "grey.300", position: "absolute" }}
                                            />
                                            <CircularProgress
                                                variant="determinate"
                                                value={(completedSteps / 3) * 100}
                                                size={28}
                                                thickness={4}
                                            />
                                        </Box>
                                    </Else>
                                </If>
                            </Stack>
                            <Divider />
                            <GetStartedStepRow
                                icon="icon-park-twotone:mind-mapping"
                                label="1. Map your skills and trades"
                                complete={hasSkillMaps}
                                actionLabel="Start skill mapping"
                                onAction={hasSkillMaps ? goToSkillMappings : handleCreateSkillMap}
                            />
                            <Divider sx={{ borderStyle: "dashed" }} />
                            <GetStartedStepRow
                                icon="solar:user-id-broken"
                                label="2. Set your labour and cost rates"
                                complete={hasRateCards}
                                actionLabel="Set up rate card"
                                onAction={hasRateCards ? goToRateCards : handleCreateRateCard}
                                disabled={!hasSkillMaps}
                            />
                            <Divider sx={{ borderStyle: "dashed" }} />
                            <GetStartedStepRow
                                icon="qlementine-icons:money-16"
                                label="3. Build your first scenario"
                                complete={hasScenarios}
                                actionLabel="Build scenario"
                                onAction={hasScenarios ? goToScenarios : goToCreateScenario}
                                disabled={!hasRateCards}
                            />
                        </CardContent>
                    </Card>
                </Fade>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={toggleOpen}
                    sx={{
                        borderRadius: "50px",
                        px: 2.5,
                        py: 1,
                        boxShadow: "0px 8px 16px 0px rgba(0,0,0,0.15)",
                        textTransform: "none",
                        fontWeight: 600,
                    }}
                    startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                >
                    <If value={open}>
                        Hide
                        <Else>Get Started {completedSteps}/3</Else>
                    </If>
                </Button>
            </Box>
        </ClickAwayListener>
    )

    function toggleOpen() {
        setOpen(!open)
    }

    function handleClose() {
        if (open) setOpen(false)
    }

    function handleDismiss() {
        setDismissed(true)
    }

    function goToSkillMappings() {
        navigate("/app/optimiser?optimiserTab=skill-mappings")
    }

    function goToRateCards() {
        navigate("/app/optimiser?optimiserTab=rate-cards")
    }

    function goToScenarios() {
        navigate("/app/optimiser?optimiserTab=scenarios")
    }

    function goToCreateScenario() {
        navigate("/app/optimiser/scenarios/create")
    }
}
